import React, { useState, useMemo } from 'react';
import { Modal, Alert } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  GridGetterFunction,
  GridCountFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { CreateUpdateContractDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import Button from '../../../common/components/Button/Button';
import { ContractSearch } from '../../components/Contracts/ContractSearch';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useNavigation } from '../../../common/navigation';

interface SelectContractToUpdateProps {
  contract: CreateUpdateContractDto;
  contractId: string;
  visible?: boolean;
}

export const SelectContractToUpdate: React.FC<
  SelectContractToUpdateProps
> = props => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const nav = useNavigation();
  const { t, tk } = useTranslation('projectLng');
  const [selectedContractId, setSelectedContractId] = useState<
    string | undefined
  >(undefined);
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setShow(false);
  };

  const onRowClick = (cid: string) => {
    setSelectedContractId(cid);
  };

  const getData = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.contracts.getToUpdateByPortion(
          t,
          tk,
          props.contractId,
          props.contract.tv,
          props.contract.projectId,
          props.contract.clientId,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.contracts, props.contract, t, tk, props.contractId],
  );

  const getCount = useMemo(
    () =>
      ((filterText, filterColumns) =>
        pok.contracts.getToUpdateCount(
          props.contractId,
          props.contract.tv,
          props.contract.projectId,
          props.contract.clientId,
          filterText,
          filterColumns,
        )) as GridCountFunction,
    [props.contract, pok.contracts, props.contractId],
  );

  const onSelectContract = () => {
    if (selectedContractId) {
      setIsLoading(true);

      pok.contracts
        .updateAndRemove(props.contractId, selectedContractId)
        .then(() => {
          if (props.contract.tv) {
            nav.contractEditorTV(selectedContractId);
          } else {
            nav.contractEditor(selectedContractId);
          }
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        })
        .finally(() => setIsLoading(false));
      setShow(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-end">
        <Button onClick={() => setShow(true)} variant="outline-success">
          {t(tk.contract.selectToUpdate)}
        </Button>
      </div>
      {show && (
        <Modal
          show={show}
          onHide={handleClose}
          centered
          keyboard={true}
          backdrop="static"
          backdropClassName="modal-on-modal-backdrop"
          className="modal-on-modal"
          dialogClassName="modal-90w"
        >
          <Modal.Header closeButton>
            <Modal.Title>{t(tk.contract.selectToUpdate)}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ContractSearch
              getCount={getCount}
              getData={getData}
              enableSingleSelect={true}
              selectedKeys={[selectedContractId || '']}
              onRowClick={onRowClick}
              displayStatus={true}
              showNewWindowButton={true}
            />
            <Alert variant="info">{t(tk.contract.warningSelectToUpdate)}</Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose}>
              {t(tk.button.close)}
            </Button>
            <Button
              variant="primary"
              onClick={onSelectContract}
              isLoading={isLoading}
            >
              {t(tk.button.select)}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};
